/**
 * RESIZE PHOTOS - Storage cleanup utility
 * 
 * Downloads photos, resizes to max 1500x1500, and re-uploads.
 * This overwrites the original files to save storage space.
 * 
 * Add to routes, run once, then remove.
 * Route suggestion: /resize-photos
 */

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { ref, getDownloadURL, uploadBytes, getMetadata } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';
import { ArrowLeft, ImageDown, Check, Loader2, AlertCircle, Pause, Play } from 'lucide-react';

const MAX_SIZE = 1500; // Max dimension in pixels
// const MIN_SIZE_TO_PROCESS = 1500; // Only resize if larger than this

interface PhotoInfo {
  itemId: string;
  photoIndex: number;
  storagePath: string;
  url: string;
}

interface ProcessResult {
  processed: number;
  resized: number;
  skipped: number;
  errors: number;
  bytesSaved: number;
}

export default function ResizePhotos() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isPausedRef = useRef(false);
  
  const [stage, setStage] = useState<'ready' | 'scanning' | 'processing' | 'paused' | 'complete'>('ready');
  const [photos, setPhotos] = useState<PhotoInfo[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentPhoto, setCurrentPhoto] = useState<string>('');
  const [result, setResult] = useState<ProcessResult>({
    processed: 0,
    resized: 0,
    skipped: 0,
    errors: 0,
    bytesSaved: 0
  });
  const [error, setError] = useState<string | null>(null);

  // Extract storage path from Firebase URL
  const getStoragePathFromUrl = (url: string): string | null => {
    try {
      // Firebase Storage URLs contain the path after /o/ and before ?
      const match = url.match(/\/o\/(.+?)\?/);
      if (match) {
        return decodeURIComponent(match[1]);
      }
      return null;
    } catch {
      return null;
    }
  };

  // Scan for all photos
  const scanPhotos = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      setError('You must be logged in');
      return;
    }

    setStage('scanning');
    setError(null);

    try {
      const itemsRef = collection(db, 'users', currentUser.uid, 'items');
      const snapshot = await getDocs(itemsRef);
      
      const allPhotos: PhotoInfo[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        
        // Check photos array
        if (data.photos && Array.isArray(data.photos)) {
          data.photos.forEach((photo: any, index: number) => {
            const url = photo.url || photo;
            if (typeof url === 'string' && url.includes('firebase')) {
              const storagePath = getStoragePathFromUrl(url);
              if (storagePath) {
                allPhotos.push({
                  itemId: docSnap.id,
                  photoIndex: index,
                  storagePath,
                  url
                });
              }
            }
          });
        }
        
        // Check legacy photoUrl field
        if (data.photoUrl && typeof data.photoUrl === 'string' && data.photoUrl.includes('firebase')) {
          const storagePath = getStoragePathFromUrl(data.photoUrl);
          if (storagePath) {
            allPhotos.push({
              itemId: docSnap.id,
              photoIndex: -1, // -1 indicates legacy photoUrl field
              storagePath,
              url: data.photoUrl
            });
          }
        }
      });

      setPhotos(allPhotos);
      setStage('ready');
      
    } catch (err) {
      console.error('Error scanning:', err);
      setError(`Scan failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setStage('ready');
    }
  };

  // Resize a single image
  const resizeImage = (img: HTMLImageElement): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        resolve(null);
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }

      // Check if resize is needed
      if (img.width <= MAX_SIZE && img.height <= MAX_SIZE) {
        resolve(null); // No resize needed
        return;
      }

      // Calculate new dimensions maintaining aspect ratio
      let newWidth = img.width;
      let newHeight = img.height;

      if (img.width > img.height) {
        if (img.width > MAX_SIZE) {
          newWidth = MAX_SIZE;
          newHeight = Math.round((img.height / img.width) * MAX_SIZE);
        }
      } else {
        if (img.height > MAX_SIZE) {
          newHeight = MAX_SIZE;
          newWidth = Math.round((img.width / img.height) * MAX_SIZE);
        }
      }

      canvas.width = newWidth;
      canvas.height = newHeight;

      // Use high quality resizing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      canvas.toBlob(
        (blob) => resolve(blob),
        'image/jpeg',
        0.85 // Good quality, reasonable file size
      );
    });
  };

  // Process all photos
  const processPhotos = async () => {
    if (photos.length === 0) {
      await scanPhotos();
      return;
    }

    setStage('processing');
    isPausedRef.current = false;

    const newResult = { ...result };

    for (let i = currentIndex; i < photos.length; i++) {
      // Check for pause
      if (isPausedRef.current) {
        setCurrentIndex(i);
        setStage('paused');
        return;
      }

      const photo = photos[i];
      setCurrentIndex(i);
      setCurrentPhoto(`${photo.storagePath.split('/').pop()}`);

      try {
        // Get current file metadata for size comparison
        const storageRef = ref(storage, photo.storagePath);
        let originalSize = 0;
        
        try {
          const metadata = await getMetadata(storageRef);
          originalSize = metadata.size || 0;
        } catch {
          // Metadata fetch failed, continue anyway
        }

        // Download the image
        const url = await getDownloadURL(storageRef);
        
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = url;
        });

        // Resize if needed
        const resizedBlob = await resizeImage(img);

        if (resizedBlob) {
          // Upload resized version
          await uploadBytes(storageRef, resizedBlob, {
            contentType: 'image/jpeg'
          });

          const bytesSaved = originalSize - resizedBlob.size;
          newResult.resized++;
          newResult.bytesSaved += Math.max(0, bytesSaved);
        } else {
          newResult.skipped++;
        }

        newResult.processed++;

      } catch (err) {
        console.error(`Error processing ${photo.storagePath}:`, err);
        newResult.errors++;
        newResult.processed++;
      }

      setResult({ ...newResult });

      // Small delay to prevent overwhelming the browser
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setResult(newResult);
    setStage('complete');
  };

  const handlePause = () => {
    isPausedRef.current = true;
  };

  const handleResume = () => {
    processPhotos();
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* Hidden canvas for resizing */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center gap-2 text-on-bg transition-default font-body"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        {/* Header */}
        <div className="glass-card p-6 mb-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500 mb-4">
            <ImageDown className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-glass mb-2">Resize Photos</h1>
          <p className="text-glass-secondary text-sm">
            Resize photos to max {MAX_SIZE}x{MAX_SIZE}px to save storage
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="glass-card p-4 mb-6 border-l-4 border-red-500 bg-red-500/10">
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Ready Stage */}
        {stage === 'ready' && (
          <div className="glass-card p-6">
            {photos.length === 0 ? (
              <>
                <p className="text-glass-secondary mb-6 text-center">
                  First, let's scan your collection to find all photos.
                </p>
                <button
                  onClick={scanPhotos}
                  className="w-full px-6 py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 transition-default"
                >
                  Scan for Photos
                </button>
              </>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-emerald-400">{photos.length}</div>
                  <div className="text-glass-secondary">photos found</div>
                </div>
                <p className="text-glass-secondary text-sm mb-6 text-center">
                  Photos larger than {MAX_SIZE}px will be resized. Smaller photos will be skipped.
                  This may take a while - you can pause and resume anytime.
                </p>
                <button
                  onClick={processPhotos}
                  className="w-full px-6 py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 transition-default flex items-center justify-center gap-2"
                >
                  <ImageDown className="w-5 h-5" />
                  Start Resizing
                </button>
              </>
            )}
          </div>
        )}

        {/* Scanning Stage */}
        {stage === 'scanning' && (
          <div className="glass-card p-8 text-center">
            <Loader2 className="w-12 h-12 text-emerald-400 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-glass mb-2">Scanning...</h2>
            <p className="text-glass-secondary">Finding all photos in your collection</p>
          </div>
        )}

        {/* Processing Stage */}
        {(stage === 'processing' || stage === 'paused') && (
          <div className="glass-card p-6">
            <div className="text-center mb-4">
              {stage === 'processing' ? (
                <Loader2 className="w-10 h-10 text-emerald-400 animate-spin mx-auto mb-2" />
              ) : (
                <Pause className="w-10 h-10 text-amber-400 mx-auto mb-2" />
              )}
              <h2 className="text-lg font-bold text-glass">
                {stage === 'processing' ? 'Processing...' : 'Paused'}
              </h2>
              <p className="text-glass-muted text-sm truncate max-w-xs mx-auto">
                {currentPhoto}
              </p>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-slate-700 rounded-full h-3 mb-2">
              <div 
                className="bg-emerald-500 h-3 rounded-full transition-all"
                style={{ width: `${photos.length > 0 ? ((currentIndex + 1) / photos.length) * 100 : 0}%` }}
              />
            </div>
            <p className="text-glass-muted text-sm text-center mb-4">
              {currentIndex + 1} / {photos.length}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
              <div className="bg-emerald-500/20 rounded-lg p-2 text-center">
                <div className="font-bold text-emerald-400">{result.resized}</div>
                <div className="text-emerald-300/70">Resized</div>
              </div>
              <div className="bg-slate-500/20 rounded-lg p-2 text-center">
                <div className="font-bold text-slate-400">{result.skipped}</div>
                <div className="text-slate-300/70">Skipped</div>
              </div>
              <div className="bg-indigo-500/20 rounded-lg p-2 text-center col-span-2">
                <div className="font-bold text-indigo-400">{formatBytes(result.bytesSaved)}</div>
                <div className="text-indigo-300/70">Saved so far</div>
              </div>
            </div>

            {/* Pause/Resume button */}
            {stage === 'processing' ? (
              <button
                onClick={handlePause}
                className="w-full px-6 py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-default flex items-center justify-center gap-2"
              >
                <Pause className="w-5 h-5" />
                Pause
              </button>
            ) : (
              <button
                onClick={handleResume}
                className="w-full px-6 py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 transition-default flex items-center justify-center gap-2"
              >
                <Play className="w-5 h-5" />
                Resume
              </button>
            )}
          </div>
        )}

        {/* Complete Stage */}
        {stage === 'complete' && (
          <div className="glass-card p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500 mb-4">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-glass mb-4">Complete!</h2>
            
            <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
              <div className="bg-emerald-500/20 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-emerald-400">{result.resized}</div>
                <div className="text-emerald-300/70">Resized</div>
              </div>
              <div className="bg-slate-500/20 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-slate-400">{result.skipped}</div>
                <div className="text-slate-300/70">Already Small</div>
              </div>
              {result.errors > 0 && (
                <div className="bg-red-500/20 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-400">{result.errors}</div>
                  <div className="text-red-300/70">Errors</div>
                </div>
              )}
              <div className={`bg-indigo-500/20 rounded-lg p-3 text-center ${result.errors === 0 ? 'col-span-2' : ''}`}>
                <div className="text-2xl font-bold text-indigo-400">{formatBytes(result.bytesSaved)}</div>
                <div className="text-indigo-300/70">Storage Saved</div>
              </div>
            </div>

            <button
              onClick={() => navigate('/collection')}
              className="w-full px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-default"
            >
              Back to Collection
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
